"use client";

import {
  AnimatePresence,
  m as motion,
  useReducedMotion,
} from "framer-motion";
import { useSyncExternalStore } from "react";

type Labels = {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
  until: string;
};

type Props = {
  deadline: string;
  labels: Labels;
};

function pad(n: number | null): string {
  if (n === null) return "—";
  return n.toString().padStart(2, "0");
}

function getDigits(value: number | null): [string, string] {
  if (value === null) return ["—", "—"];
  const padded = pad(value);
  return [padded[0]!, padded[1]!];
}

// Time ticks once per second. Cached at module scope so getSnapshot returns
// a stable reference between subscription events — useSyncExternalStore
// requires that getSnapshot only changes when the store actually changes.
let cachedNow: number | null = null;

function subscribeToTick(callback: () => void) {
  cachedNow = Date.now();
  callback();
  const id = window.setInterval(() => {
    cachedNow = Date.now();
    callback();
  }, 1000);
  return () => window.clearInterval(id);
}

function getNowSnapshot(): number | null {
  return cachedNow;
}

function getServerNowSnapshot(): number | null {
  return null;
}

export function Countdown({ deadline, labels }: Props) {
  const target = new Date(deadline).getTime();
  const now = useSyncExternalStore(
    subscribeToTick,
    getNowSnapshot,
    getServerNowSnapshot,
  );
  const prefersReduced = useReducedMotion();

  const diff = now === null ? null : Math.max(0, target - now);
  const days = diff === null ? null : Math.floor(diff / 86_400_000);
  const hours = diff === null ? null : Math.floor((diff / 3_600_000) % 24);
  const minutes = diff === null ? null : Math.floor((diff / 60_000) % 60);
  const seconds = diff === null ? null : Math.floor((diff / 1000) % 60);

  const reduced = Boolean(prefersReduced);

  const cells = [
    { value: days, label: labels.days },
    { value: hours, label: labels.hours },
    { value: minutes, label: labels.minutes },
    { value: seconds, label: labels.seconds },
  ];

  return (
    <motion.div
      className="relative overflow-hidden rounded-2xl border p-4 sm:p-5 md:p-7"
      style={{
        borderColor: "var(--border-strong)",
        background:
          "linear-gradient(180deg, rgba(18,20,30,0.55) 0%, rgba(8,10,16,0.55) 100%)",
      }}
      initial={reduced ? false : { opacity: 0, y: 12 }}
      whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      aria-live="polite"
    >
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -inset-x-20 -inset-y-32 -z-10"
        style={{
          background:
            "radial-gradient(45% 55% at 50% 50%, rgba(34,211,238,0.12) 0%, transparent 70%)",
        }}
        animate={reduced ? undefined : { opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 4.5, ease: "easeInOut", repeat: Infinity }}
      />

      <div className="flex flex-col gap-4 sm:gap-5 lg:flex-row lg:items-center lg:gap-7">
        <span
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase text-fg3 sm:text-[11px]"
          style={{ letterSpacing: "0.2em" }}
        >
          <motion.span
            aria-hidden
            className="inline-block h-[6px] w-[6px] rounded-full"
            style={{ background: "var(--brand-cyan)" }}
            animate={
              reduced
                ? undefined
                : {
                    opacity: [0.45, 1, 0.45],
                    boxShadow: [
                      "0 0 0px rgba(34,211,238,0)",
                      "0 0 14px rgba(34,211,238,0.9)",
                      "0 0 0px rgba(34,211,238,0)",
                    ],
                  }
            }
            transition={{ duration: 1.6, ease: "easeInOut", repeat: Infinity }}
          />
          [{labels.until}]
        </span>

        <div
          className="grid w-full grid-cols-4 gap-1 sm:gap-3 lg:ml-auto lg:w-auto lg:gap-4"
          suppressHydrationWarning
        >
          {cells.map((cell) => {
            const [d1, d2] = getDigits(cell.value);
            return (
              <div key={cell.label} className="flex flex-col items-center">
                <div className="flex items-stretch gap-1 sm:gap-1.5">
                  <FlipDigit digit={d1} reduced={reduced} />
                  <FlipDigit digit={d2} reduced={reduced} />
                </div>
                <span
                  className="mt-3 font-mono uppercase text-fg3"
                  style={{
                    fontSize: "clamp(8px, 1.5vw, 10px)",
                    letterSpacing: "0.2em",
                    whiteSpace: "nowrap",
                  }}
                >
                  {cell.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

type FlipDigitProps = { digit: string; reduced: boolean };

function FlipDigit({ digit, reduced }: FlipDigitProps) {
  return (
    <div
      className="relative"
      style={{
        perspective: "700px",
        width: "clamp(22px, 6vw, 50px)",
        height: "clamp(34px, 9vw, 70px)",
      }}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.div
          key={digit}
          initial={
            reduced
              ? { opacity: 0 }
              : { rotateX: 90, opacity: 0, y: "-25%" }
          }
          animate={
            reduced
              ? { opacity: 1 }
              : { rotateX: 0, opacity: 1, y: "0%" }
          }
          exit={
            reduced
              ? { opacity: 0 }
              : { rotateX: -90, opacity: 0, y: "25%" }
          }
          transition={{
            duration: reduced ? 0.18 : 0.55,
            ease: [0.55, 0.05, 0.25, 1],
          }}
          className="absolute inset-0"
          style={{
            transformStyle: "preserve-3d",
            transformOrigin: "center center",
            backfaceVisibility: "hidden",
          }}
        >
          <DigitCard digit={digit} />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function DigitCard({ digit }: { digit: string }) {
  return (
    <div
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-lg"
      style={{
        background:
          "linear-gradient(180deg, rgba(30,34,46,1) 0%, rgba(14,16,24,1) 49.9%, rgba(20,22,32,1) 50.1%, rgba(8,10,16,1) 100%)",
        boxShadow: [
          "inset 0 1px 0 rgba(255,255,255,0.10)",
          "inset 0 -1px 0 rgba(0,0,0,0.5)",
          "inset 0 0 0 1px rgba(255,255,255,0.05)",
          "0 8px 22px rgba(0,0,0,0.55)",
          "0 1px 0 rgba(59,126,255,0.06)",
        ].join(", "),
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute left-1 right-1 top-1/2 h-px -translate-y-px"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.75) 50%, transparent 100%)",
          boxShadow: "0 1px 0 rgba(255,255,255,0.04)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-lg"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 0%, rgba(59,126,255,0.08) 0%, transparent 60%)",
        }}
      />
      <span
        className="relative font-display font-bold tabular-nums"
        style={{
          fontSize: "clamp(20px, 5.5vw, 44px)",
          lineHeight: 1,
          letterSpacing: "-0.02em",
          color: "var(--fg1)",
          textShadow:
            "0 2px 4px rgba(0,0,0,0.6), 0 0 18px rgba(59,126,255,0.15)",
        }}
      >
        {digit}
      </span>
    </div>
  );
}
